import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const Footer: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  // 获取当前语言作为链接前缀
  const currentLang = i18n.language;
  const homeLink = currentLang === 'en' ? '/' : `/${currentLang}/`;

  const handleLanguageChange = (lang: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    i18n.changeLanguage(lang);
    navigate(lang === 'en' ? '/' : `/${lang}/`);
  };

  return (
    <footer className="bg-gray-50 dark:bg-gray-900">
      <div className="mx-auto max-w-screen-xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          {/* About Our Service + Logo */}
          <div>
            <div className="flex items-center mb-4">
              <img src="/logo.svg" alt="TempMail Logo" className="h-8 w-8 mr-2" />
              <span className="text-gray-700 dark:text-gray-300 font-semibold">TempMail</span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">
              TempMail provides secure, disposable email addresses to protect your privacy online. 
              Our service helps you avoid spam and keep your real inbox clean.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Navigation</p>
            <nav className="mt-4">
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to={homeLink} className="text-gray-500 dark:text-gray-400 hover:text-emerald-500">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-gray-500 dark:text-gray-400 hover:text-emerald-500">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/privacy-policy" className="text-gray-500 dark:text-gray-400 hover:text-emerald-500">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-gray-500 dark:text-gray-400 hover:text-emerald-500">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* External Links */}
          <div>
            <p className="font-medium text-gray-900 dark:text-white">External Links</p>
            <nav className="mt-4">
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://markdown.fastbytelab.com"
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Markdown Editor
                  </a>
                </li>
                <li>
                  <a
                    href="https://x.com/Tempmail_Love"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500 inline-flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    Follow us on X
                  </a>
                </li>
                <li>
                  <a
                    href="https://startupfa.me/s/secure-anonymous-tempmail?utm_source=tempmail.love"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500 block"
                  >
                    <img
                      src="https://startupfa.me/badges/featured-badge-small.webp"
                      alt="SecureTempMail - No registration required, safe and anonymous | Startup Fame"
                      width="224"
                      height="36"
                      className="max-w-full"
                    />
                  </a>
                </li>
                <li>
                  <a 
                    href="https://similarlabs.com/?ref=embed" 
                    target="_blank"
                    >
                    <img src="https://similarlabs.com/similarlabs-embed-badge-light.svg" alt="SimilarLabs Embed Badge" />
                  </a>
                  <a href='https://sprunkiretake.world/' title='Sprunki Retake World'>Sprunki Retake World</a>
                  <a href="https://sprunkid.com/">Sprunkid</a>
                </li>
              </ul>
            </nav>
          </div>

          {/* Available Languages */}
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Available Languages</p>
            <nav className="mt-4">
              <ul className="grid grid-cols-2 gap-1 text-sm">
                <li>
                  <a
                    href="/"
                    onClick={handleLanguageChange('en')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    English
                  </a>
                </li>
                <li>
                  <a
                    href="/zh/"
                    onClick={handleLanguageChange('zh')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    中文
                  </a>
                </li>
                <li>
                  <a
                    href="/ja/"
                    onClick={handleLanguageChange('ja')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    日本語
                  </a>
                </li>
                <li>
                  <a
                    href="/ko/"
                    onClick={handleLanguageChange('ko')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    한국어
                  </a>
                </li>
                <li>
                  <a
                    href="/ar/"
                    onClick={handleLanguageChange('ar')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    العربية
                  </a>
                </li>
                <li>
                  <a
                    href="/fr/"
                    onClick={handleLanguageChange('fr')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    Français
                  </a>
                </li>
                <li>
                  <a
                    href="/de/"
                    onClick={handleLanguageChange('de')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    Deutsch
                  </a>
                </li>
                <li>
                  <a
                    href="/pt/"
                    onClick={handleLanguageChange('pt')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    Português
                  </a>
                </li>
                <li>
                  <a
                    href="/ru/"
                    onClick={handleLanguageChange('ru')}
                    className="text-gray-500 dark:text-gray-400 hover:text-emerald-500"
                  >
                    Русский
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="mt-8 border-t border-gray-100 dark:border-gray-800 pt-8">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} TempMail. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
